// ./src/hooks/useUserNotifications.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  UserNotification,
  UserNotificationService,
  UserNotificationFilter,
  UserNotificationInput,
  Pagination,
} from "@/utils/api";
import { SortItem } from "@/utils/api";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseUserNotificationsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<UserNotificationFilter>;

  /** Initial sort configuration */
  initialSort?: SortItem[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Current user ID - data reloads when this changes */
  userId?: string | null;
}

export interface UseUserNotificationsReturn {
  // Data state
  userNotification: UserNotification | null;
  userNotifications: UserNotification[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<UserNotificationFilter>;
  sortBy: SortItem[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingId: string | null;
  selectedUserNotification: UserNotification | null;

  // Computed values
  unreadCount: number;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<UserNotificationFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<UserNotification[]>;
  create: (params: UserNotificationInput) => Promise<UserNotification | null>;
  update: (
    id: string,
    params: UserNotificationInput
  ) => Promise<UserNotification | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  markAsRead: (id: string) => Promise<UserNotification | null>;
  markManyAsRead: (ids: string[]) => Promise<boolean>;
  markAllAsRead: () => Promise<boolean>;

  // UI state management
  setSelectedUserNotification: (notification: UserNotification | null) => void;

  // Helper operations
  reset: () => void;

  // Lookup helpers
  getUserNotificationById: (
    id: string | undefined
  ) => UserNotification | undefined;
  getUnreadNotifications: () => UserNotification[];

  // Filtering helpers
  listUnread: (
    filter?: Partial<UserNotificationFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<void>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortItem[] = [{ name: "createdAt", order: "DESC" }];

export const useUserNotifications = (
  options: UseUserNotificationsOptions = {}
): UseUserNotificationsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    userId = null,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortItem[]>(initialSort);
  const initialFiltersRef = useRef(initialFilters);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  useEffect(() => {
    initialFiltersRef.current = initialFilters;
  }, [initialFilters]);

  // Data state
  const [userNotification, setUserNotification] =
    useState<UserNotification | null>(null);
  const [userNotifications, setUserNotifications] = useState<
    UserNotification[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<UserNotificationFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [selectedUserNotification, setSelectedUserNotification] =
    useState<UserNotification | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => userNotifications.length === 0 && !loading,
    [userNotifications.length, loading]
  );

  const unreadCount = useMemo(
    () => userNotifications.filter((n) => !n.readAt).length,
    [userNotifications]
  );

  // Helper function to update item in list if it exists, or add it if not
  const updateOrAddInList = useCallback((updatedItem: UserNotification) => {
    setUserNotifications((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.id === updatedItem.id
      );
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = updatedItem;
        return newItems;
      } else {
        return [updatedItem, ...prev];
      }
    });
  }, []);

  // Helper function to remove item from list if it exists
  const removeFromList = useCallback((id: string) => {
    setUserNotifications((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Helper function to remove multiple items from list
  const removeManyFromList = useCallback((ids: string[]) => {
    const idsSet = new Set(ids);
    setUserNotifications((prev) =>
      prev.filter((item) => !idsSet.has(item.id || ""))
    );
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setUserNotification(null);
    setUserNotifications([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingId(null);
    setSelectedUserNotification(null);
  }, []);

  // Get single user notification (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await UserNotificationService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = (result.data as UserNotification[])[0];
          setUserNotification(data);
          updateOrAddInList(data);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load notification";
          setLastError(errorMessage);
          setUserNotification(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load notification";
        setLastError(errorMessage);
        setUserNotification(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // List user notifications (matching API method name)
  const list = useCallback(
    async (
      filter: Partial<UserNotificationFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortItem[],
      shouldReset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (shouldReset) {
          setUserNotifications([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => sort);

        const result = await UserNotificationService.list(
          filter as UserNotificationFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as UserNotification[])
            : [];

          if (isAppending) {
            setUserNotifications((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(item.id)
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            setUserNotifications(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load notifications";
          setLastError(errorMessage);
          if (!isAppending) setUserNotifications([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load notifications";
        setLastError(errorMessage);
        if (!isAppending) setUserNotifications([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many user notifications by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<UserNotification[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await UserNotificationService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedItems = Array.isArray(result.data)
            ? (result.data as UserNotification[])
            : [];
          loadedItems.forEach((item) => {
            updateOrAddInList(item);
          });
          setLastError(null);
          return loadedItems;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load notifications";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load notifications";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // Create user notification (matching API method name)
  const create = useCallback(
    async (params: UserNotificationInput): Promise<UserNotification | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await UserNotificationService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItem = (result.data as UserNotification[])[0];
          setUserNotification(newItem);
          setUserNotifications((prev) => [newItem, ...prev]);
          setLastError(null);
          return newItem;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create notification";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create notification";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update user notification (matching API method name)
  const update = useCallback(
    async (
      id: string,
      params: UserNotificationInput
    ): Promise<UserNotification | null> => {
      if (!id) {
        setLastError("Notification ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await UserNotificationService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as UserNotification[])[0];
          setUserNotification(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          return updatedItem;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update notification";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update notification";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList]
  );

  // Remove user notification (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Notification ID is required for removal");
        return false;
      }

      setRemovingId(id);
      setLastError(null);

      try {
        const result = await UserNotificationService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setUserNotification(null);
          removeFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove notification";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove notification";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingId(null);
      }
    },
    [removeFromList]
  );

  // Remove many user notifications (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError("At least one notification ID is required for removal");
        return false;
      }

      setLastError(null);

      try {
        const result = await UserNotificationService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setUserNotification(null);
          removeManyFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove notifications";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove notifications";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeManyFromList]
  );

  // Load more user notifications
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh user notifications
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Mark a single notification as read
  const markAsRead = useCallback(
    async (id: string): Promise<UserNotification | null> => {
      if (!id) {
        setLastError("Notification ID is required");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await UserNotificationService.markAsRead(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as UserNotification[])[0];
          setUserNotification(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          return updatedItem;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to mark notification as read";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to mark notification as read";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList]
  );

  // Mark multiple notifications as read
  const markManyAsRead = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError("At least one notification ID is required");
        return false;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const results = await UserNotificationService.markManyAsRead(ids);

        const allSucceeded = results.every(
          (result) => result.code === OP_RESULT_CODES.OK
        );

        if (allSucceeded) {
          // Update local state for all successfully marked notifications
          results.forEach((result) => {
            if (result.code === OP_RESULT_CODES.OK && result.data) {
              const updatedItem = (result.data as UserNotification[])[0];
              updateOrAddInList(updatedItem);
            }
          });
          setLastError(null);
          return true;
        } else {
          setLastError("Failed to mark some notifications as read");
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to mark notifications as read";
        setLastError(errorMessage);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList]
  );

  // Mark all loaded unread notifications as read
  const markAllAsRead = useCallback(async (): Promise<boolean> => {
    const unreadIds = userNotifications
      .filter((n) => !n.readAt && n.id)
      .map((n) => n.id as string);

    if (unreadIds.length === 0) {
      return true;
    }

    return markManyAsRead(unreadIds);
  }, [userNotifications, markManyAsRead]);

  // Lookup helper - find by ID
  const getUserNotificationById = useCallback(
    (id: string | undefined): UserNotification | undefined => {
      if (!id) return undefined;
      return userNotifications.find((item) => item.id === id);
    },
    [userNotifications]
  );

  // Helper to get unread notifications from loaded list
  const getUnreadNotifications = useCallback((): UserNotification[] => {
    return userNotifications.filter((n) => !n.readAt);
  }, [userNotifications]);

  // Convenience method to list only unread notifications
  const listUnread = useCallback(
    async (
      filter: Partial<UserNotificationFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortItem[],
      shouldReset: boolean = false
    ) => {
      const unreadFilter: Partial<UserNotificationFilter> = {
        ...filter,
        isRead: false,
      };
      await list(unreadFilter, pagination, sortByParams, shouldReset);
    },
    [list]
  );

  // Handle user change - reset and reload data when userId changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          sortByRef.current,
          true
        );
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          sortByRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  return {
    userNotification,
    userNotifications,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingId,
    selectedUserNotification,
    unreadCount,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    loadMore,
    refresh,
    markAsRead,
    markManyAsRead,
    markAllAsRead,
    setSelectedUserNotification,
    reset,
    getUserNotificationById,
    getUnreadNotifications,
    listUnread,
    lastError,
    clearError,
  };
};
